import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { useFormikContext } from 'formik';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

const ProjectResourcesTable = ({ resources, loading = false, editable = false }) => {
    const [selectedIds, setSelectedIds] = useState([]);

    return (
        <div className="inbud-table_wrapper tw-min-h-0 tw-overflow-y-auto">
            <table className="inbud-table tw-table-fixed">
                <thead className="tw-sticky tw-top-0 tw-z-10">
                    <tr>
                        {editable && (
                            <th className="tw-w-9">
                                {!!selectedIds.length && (
                                    <div className="tw-rounded-full tw-h-5 tw-w-5 tw-flex tw-items-center tw-justify-center tw-text-2xs tw-text-white tw-bg-red-75">
                                        {selectedIds.length}
                                    </div>
                                )}
                            </th>
                        )}
                        <th>{$t('Position')}</th>
                        <th className="tw-w-28">{$t('Type')}</th>
                        <th>{$t('Name')}</th>
                        <th className="tw-w-28">{$t('Start Date')}</th>
                        <th className="tw-w-28">{$t('End Date')}</th>
                    </tr>
                </thead>
                <tbody>
                    <ProjectResourcesTableRowsManager
                        resources={resources}
                        editable={editable}
                        loading={loading}
                        onSelectionChange={(selectedIds) => setSelectedIds(selectedIds)}
                    />
                </tbody>
            </table>
        </div>
    );
};

const SelectionTracker = ({ onSelectionChange, children }) => {
    const { values } = useFormikContext();
    const prev = useRef(null);

    useEffect(() => {
        const next = Object.entries(values.selected)
            .filter(([_, isSelected]) => Boolean(isSelected))
            .map(([key]) => parseInt(key, 10))
            .sort((a, b) => a - b);

        // shallow compare to avoid redundant parent updates
        const sameLength = prev.current?.length === next.length;
        const sameItems = sameLength && prev.current?.every((id, idx) => id === next[idx]);

        if (!sameItems) {
            prev.current = next;
            onSelectionChange?.(next);
        }
    }, [values.selected, onSelectionChange]);

    return <Fragment>{children}</Fragment>;
};

const ProjectResourcesTableRowsManager = ({ resources, editable, loading, onSelectionChange }) => {
    if (loading) {
        return <TableRowSkeleton rows={10} columns={editable ? 6 : 5} />;
    }

    if (!resources || resources.length === 0) {
        return (
            <tr>
                <td colSpan={editable ? 6 : 5} className="tw-text-center">
                    {$t('No resources allocated.')}
                </td>
            </tr>
        );
    }

    if (editable) {
        return (
            <SelectionTracker onSelectionChange={onSelectionChange}>
                {resources.map((departmentResource) => (
                    <DepartmentResourceRow key={departmentResource.department_id} resource={departmentResource} editable />
                ))}
            </SelectionTracker>
        );
    }

    return resources.map((departmentResource) => <DepartmentResourceRow key={departmentResource.department_id} resource={departmentResource} />);
};

const DepartmentResourceRow = ({ resource, editable = false }) => {
    const [collapsed, setCollapsed] = useState(true);
    const formik = editable ? useFormikContext() : null;

    // Get all resource IDs under this department
    const getAllResourceIds = () => {
        const ids = [];
        resource.positions?.forEach((pos) => ids.push(pos.id));
        resource.costcenters?.forEach((cc) => {
            cc.positions?.forEach((pos) => ids.push(pos.id));
        });
        return ids;
    };

    // Check if all resources are selected
    const isAllSelected = useMemo(() => {
        if (!formik) return false;
        const ids = getAllResourceIds();
        return ids.length > 0 && ids.every((id) => formik.values.selected[id]);
    }, [formik?.values.selected, resource]);

    // Check if some (but not all) resources are selected
    const isIndeterminate = useMemo(() => {
        if (!formik) return false;
        const ids = getAllResourceIds();
        const selectedCount = ids.filter((id) => formik.values.selected[id]).length;
        return selectedCount > 0 && selectedCount < ids.length;
    }, [formik?.values.selected, resource]);

    const handleDepartmentToggle = (e) => {
        e.stopPropagation();
        if (!formik) return;

        const ids = getAllResourceIds();
        const newValue = !isAllSelected;

        const newSelected = { ...formik.values.selected };
        ids.forEach((id) => {
            newSelected[id] = newValue;
        });
        formik.setFieldValue('selected', newSelected);
    };

    return (
        <Fragment>
            <tr onClick={() => setCollapsed(!collapsed)}>
                {editable && (
                    <td onClick={(e) => e.stopPropagation()}>
                        <div className="tw-flex tw-justify-center tw-items-center">
                            <CheckboxInput checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleDepartmentToggle} />
                        </div>
                    </td>
                )}
                <td colSpan="5" className="tw-cursor-pointer">
                    <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-font-semibold">
                        <div>{resource.department_name}</div>
                        <div>{collapsed ? <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" />}</div>
                    </div>
                </td>
            </tr>
            {!collapsed && (
                <Fragment>
                    {resource.costcenters.map((costcenterResource) => (
                        <CostcenterResourceRow key={costcenterResource.costcenter_id} resource={costcenterResource} indent={1} editable={editable} />
                    ))}
                    {resource.positions.map((res) => (
                        <ResourceRow key={res.id} resource={res} indent={1} editable={editable} />
                    ))}
                </Fragment>
            )}
        </Fragment>
    );
};

const CostcenterResourceRow = ({ resource, indent, editable = false }) => {
    const [collapsed, setCollapsed] = useState(true);
    const formik = editable ? useFormikContext() : null;

    // Get all resource IDs under this cost center
    const getAllResourceIds = () => {
        return resource.positions?.map((pos) => pos.id) || [];
    };

    // Check if all resources are selected
    const isAllSelected = useMemo(() => {
        if (!formik) return false;
        const ids = getAllResourceIds();
        return ids.length > 0 && ids.every((id) => formik.values.selected[id]);
    }, [formik?.values.selected, resource]);

    // Check if some (but not all) resources are selected
    const isIndeterminate = useMemo(() => {
        if (!formik) return false;
        const ids = getAllResourceIds();
        const selectedCount = ids.filter((id) => formik.values.selected[id]).length;
        return selectedCount > 0 && selectedCount < ids.length;
    }, [formik?.values.selected, resource]);

    const handleCostcenterToggle = (e) => {
        e.stopPropagation();
        if (!formik) return;

        const ids = getAllResourceIds();
        const newValue = !isAllSelected;

        const newSelected = { ...formik.values.selected };
        ids.forEach((id) => {
            newSelected[id] = newValue;
        });
        formik.setFieldValue('selected', newSelected);
    };

    return (
        <Fragment>
            <tr onClick={() => setCollapsed(!collapsed)}>
                {editable && (
                    <td onClick={(e) => e.stopPropagation()}>
                        <div className="tw-flex tw-justify-center tw-items-center">
                            <CheckboxInput checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleCostcenterToggle} />
                        </div>
                    </td>
                )}
                <td colSpan="5" className="tw-cursor-pointer">
                    <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-font-semibold" style={{ paddingLeft: `${indent * 1}rem` }}>
                        <div>{resource.costcenter_name}</div>
                        <div>{collapsed ? <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" />}</div>
                    </div>
                </td>
            </tr>
            {!collapsed && (
                <Fragment>
                    {resource.positions.map((res) => (
                        <ResourceRow key={res.id} resource={res} indent={indent + 1} editable={editable} />
                    ))}
                </Fragment>
            )}
        </Fragment>
    );
};

const ResourceRow = ({ resource, indent, editable = false }) => {
    const formik = editable ? useFormikContext() : null;
    const fieldName = `selected.${resource.id}`;

    return (
        <tr className="*:!tw-bg-white">
            {editable && (
                <td>
                    <div className="tw-flex tw-justify-center tw-items-center">
                        <CheckboxInput
                            name={fieldName}
                            checked={formik?.values.selected[resource.id] || false}
                            onChange={(e) => {
                                formik?.setFieldValue(fieldName, e.target.checked);
                            }}
                        />
                    </div>
                </td>
            )}
            <td>
                <div style={{ paddingLeft: `${indent * 1}rem` }}>{resource.name}</div>
            </td>
            <td>{resource.type_label}</td>
            <td>{resource.position_name}</td>
            <td className="inbud-date-cell">{format(parseISO(resource.start_date), 'dd-MM-yyyy')}</td>
            <td className="inbud-date-cell">{format(parseISO(resource.end_date), 'dd-MM-yyyy')}</td>
        </tr>
    );
};

export default ProjectResourcesTable;
